// src/graphqlClient.ts
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  ApolloLink,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { Auth } from '@aws-amplify/auth';
import { paths } from 'src/routes/paths';

const apiUri = `${import.meta.env.VITE_API_URI}/graphql`;

const httpLink = new HttpLink({
  uri: apiUri,
  fetchOptions: {
    mode: 'cors',
  },
});

const authLink = setContext(async (_, { headers }) => {
  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();

    // Get selected company from localStorage
    let selectedCompanyId = null;
    try {
      const stored = localStorage.getItem('selectedCompany');
      if (stored) {
        const selectedCompany = JSON.parse(stored);
        selectedCompanyId = selectedCompany.id;
      }
    } catch (error) {
      console.warn('Error reading selected company from localStorage:', error);
    }

    return {
      headers: {
        ...headers,
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
        // Add custom header for selected company
        ...(selectedCompanyId && { 'X-Selected-Company-Id': selectedCompanyId }),
      },
    };
  } catch (error) {
    await Auth.signOut();
    window.location.href = paths.auth.amplify.login;
    return {
      headers: {
        ...headers,
      },
    };
  }
});

const loggingLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => result)
);

// Error link: ensures GraphQL errors reject the mutation Promise so catch blocks work
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`);
    });

    // For mutations, return an error observable to reject the Promise
    const isMutation = operation.query.definitions.some(
      (def) => def.kind === 'OperationDefinition' && def.operation === 'mutation'
    );
    if (isMutation) {
      const errorMessage = graphQLErrors.map((e) => e.message).join('; ');
      // Returning an observable that errors will reject the mutation Promise
      return new Observable((observer) => {
        observer.error(new Error(errorMessage));
      });
    }
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }

  // No forward(operation) here: returning forward from onError RE-EXECUTES the
  // operation (an extra hidden retry, even for mutations) and would re-enter
  // retryLink, doubling its budget. retryLink owns all retries; errors propagate.
  return undefined;
});

// Retry link: retries queries (never mutations) on network errors only.
// Placed after errorLink and before authLink so each retry re-runs auth (fresh token if one expired).
// A query can opt out with `context: { noRetry: true }`. Heavy dashboard reads that already
// take ~30s to fail would otherwise hold the error state back for minutes of retries.
const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      if (!error) return false;
      if (operation.getContext().noRetry) return false;
      const isMutation = operation.query.definitions.some(
        (def) => def.kind === 'OperationDefinition' && def.operation === 'mutation'
      );
      return !isMutation;
    },
  },
  delay: {
    initial: 300,
    max: 2000,
    jitter: true,
  },
});

// Global default: cache-and-network (stale-while-revalidate).
// Shows cached data instantly, then refreshes from network in background.
// Override per-query with 'network-only' when stale data is unacceptable.
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {},
    },
    RecommendationType: {
      fields: {
        // Embedded object with no id, read with two different selection sets:
        // Daily Overview omits `orderByDate`, the recommendations list includes
        // it. Apollo cannot tell whether the narrower result replaces or extends
        // the cached one, so it warns and discards. Merging keeps the wider
        // read's fields instead of dropping them.
        displaySummary: { merge: true },
      },
    },
  },
});

// Exported so the tour's demo client can reuse the real chain for the operations
// it deliberately lets through to the network instead of answering from fixtures.
export const networkLink = from([errorLink, retryLink, loggingLink, authLink, httpLink]);

const client = new ApolloClient({
  link: networkLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});

export default client;
